/**
 * Typed entityConfs — §6.2 of swagger-codegen-plan.md.
 *
 * `useEntity` derives up to 8 URLs from a single `endpoint` string. Nothing checked
 * that those URLs existed: a typo, a renamed controller route, or an `apiVersion`
 * that no longer matched produced a 404 at runtime and nothing at build time.
 *
 * `defineEntity()` closes that. It reconstructs the base path exactly as
 * `helpers/eGateApi.ts:getUrl()` does — `/api/{version}/{endpoint}` — and requires the
 * result to be a key of the generated `EntitySlots`. A wrong endpoint or a wrong version
 * is now a compile error at the config.
 *
 * There is no private-API branch: the cockpit generates against `swagger-full.json`
 * alone, so `/api/private/…` is not expressible here by construction.
 *
 * The slot map is generated (see gen-entities.mjs) rather than computed here with
 * template literals, because detail routes are not uniformly `/{id}`.
 */
import type { paths } from "./schema";
import type { EntitySlots, EntityBasePath } from "./entities";
import type { BaseDataType, BaseFilter, EntityConfig } from "../hooks/useEntity";

export type { EntityBasePath };

// ── path arithmetic ───────────────────────────────────────────────────────

/**
 * Read the version off a config.
 *
 * Written as `C extends { apiVersion: … }` rather than by inferring an optional
 * property: inferring from a property the object does not have yields `unknown`,
 * not `undefined`, so the `undefined`-defaulting form silently produced
 * `/api/unknown/geozones` and every entity resolved to `never`.
 */
type VersionOf<C> = C extends { apiVersion: infer V extends string } ? V : "v1";

/** Mirrors `getUrl()` in helpers/eGateApi.ts. Pure concatenation — no route guessing. */
export type BasePath<C extends { endpoint: string }> = `/api/${VersionOf<C>}/${C["endpoint"]}`;

// ── operation lookup ──────────────────────────────────────────────────────

/**
 * HTTP method backing each slot. Must stay in step with `SLOTS` in
 * scripts/api-codegen/entity-slots.mjs — that module decides which paths land in
 * `EntitySlots`, this one decides which operation is read off them.
 */
interface SlotMethod {
  page: "get";
  create: "post";
  detail: "get";
  update: "put";
  remove: "delete";
  activate: "put";
  list: "get";
  export: "get";
}

type Op<P, M> = P extends keyof paths ? (M extends keyof paths[P] ? paths[P][M] : never) : never;

/**
 * The operation behind slot `S` of base path `B`, or `never` when the API has no
 * such route. `S extends keyof EntitySlots[B]` is the guard that matters:
 * openapi-typescript v7 emits `never` for absent members rather than omitting them,
 * so testing `keyof` on the *generated slot map* — which omits absent slots
 * outright — is what keeps a missing route distinguishable from a present one.
 */
type SlotOp<B extends EntityBasePath, S extends keyof SlotMethod> = S extends keyof EntitySlots[B]
  ? Op<EntitySlots[B][S], SlotMethod[S]>
  : never;

type Json200<O> = O extends { responses: { 200: { content: { "application/json": infer R } } } } ? R : never;
type JsonBody<O> = O extends { requestBody: { content: { "application/json": infer B } } } ? B : never;
type QueryOf<O> = O extends { parameters: { query?: infer Q } } ? NonNullable<Q> : never;

/**
 * Everything the API says about one entity, keyed by base path.
 * Slots the API does not expose are `never`; test with `[T] extends [never]`.
 */
export interface EntityApi<B extends EntityBasePath> {
  page: Json200<SlotOp<B, "page">>;
  list: Json200<SlotOp<B, "list">>;
  detail: Json200<SlotOp<B, "detail">>;
  create: JsonBody<SlotOp<B, "create">>;
  update: JsonBody<SlotOp<B, "update">>;
  filter: QueryOf<SlotOp<B, "page">>;
}

/** `EntityApi` for a config, rather than for a base path. */
export type ApiOf<C extends { endpoint?: string }> = C extends { endpoint: string }
  ? BasePath<C> extends EntityBasePath
    ? EntityApi<BasePath<C>>
    : never
  : never;

/**
 * A config's operations, but only when they still describe that config.
 *
 * 🔴 This is what makes sub-lists work. Components render a related list by spreading a
 * parent config and overriding `endpoint`:
 *
 *     { ...props.conf, endpoint: `large-lists/${id}/entries` }
 *
 * `operations` comes along unchanged, still pointing at `/api/v1/large-lists`. Without this
 * comparison the operation wins, the override is silently dropped, and the entries table
 * lists parent large-lists instead — which is exactly what shipped, and what four Playwright
 * specs caught.
 *
 * Compares the three fields that *determine* the path rather than a rendered path string:
 * building one here would duplicate `basePath()` in entity-slots.mjs, and the two copies
 * would drift the day the URL scheme changes.
 */
export function resolveOperations(config: {
  endpoint?: string;
  apiVersion?: string;
  operations?: EntityOperations;
}): EntityOperations | undefined {
  const ops = config.operations;
  if (!ops) return undefined;
  const { endpoint, apiVersion } = ops.entity;
  return endpoint === config.endpoint && apiVersion === (config.apiVersion ?? "v1") ? ops : undefined;
}

// ── inference for useEntity ───────────────────────────────────────────────

/**
 * The detail DTO an entity's routes describe, for `useEntity` to infer `T` from.
 *
 * Falls back to `BaseDataType` in two cases that both mean "the document cannot answer":
 * the endpoint does not resolve (`ApiOf` is `never`), or the entity has no detail route
 * so the slot itself is `never`. Both stay compilable rather than becoming `never` and
 * poisoning every consumer.
 */
export type DetailOf<C extends { endpoint?: string }> =
  ApiOf<C> extends { detail: infer D }
    ? [D] extends [never]
      ? BaseDataType
      : D extends BaseDataType
        ? D
        : BaseDataType
    : BaseDataType;

/**
 * The page route's 200 body — what `useEntityFetch` resolves to.
 *
 * Distinct from `DetailOf` on purpose. `useEntity` used to thread a single `T` through
 * both, so call sites passed `PagedResultDto<ContainerHeaderDto>` when they wanted the
 * page and a bare DTO when they wanted the detail, and the hook could not be right for
 * both at once.
 */
export type PageOf<C extends { endpoint?: string }> =
  ApiOf<C> extends { page: infer P } ? ([P] extends [never] ? BaseDataType : P) : BaseDataType;

/** The `/list` route's 200 body — what the infinite fetch pages through. */
export type ListOf<C extends { endpoint?: string }> =
  ApiOf<C> extends { list: infer L } ? ([L] extends [never] ? BaseDataType : L) : BaseDataType;

/**
 * The row type inside a paged or partial list body.
 *
 * Structural rather than `PagedResultDto<infer I>`: the document flattens every generic
 * instantiation into its own schema (`ContainerHeaderDto_PagedResultDto`), so there is no
 * generic to match on — only the `results` array they all share.
 */
export type ItemOf<TList> = TList extends { results?: (infer I)[] }
  ? I
  : TList extends { results: (infer I)[] }
    ? I
    : TList;

/** The query type of an entity's page route, for `useEntity` to infer `TFilter` from. */
export type FilterOf<C extends { endpoint?: string }> =
  ApiOf<C> extends { filter: infer F } ? ([F] extends [never] ? BaseFilter : F) : BaseFilter;

// ── runtime operations ────────────────────────────────────────────────────

/**
 * One generated SDK function, plus the name of the path parameter it interpolates.
 *
 * The parameter travels with the call because detail-style routes are not uniformly
 * `/{id}` — 13 of them use `{customerId}`, `{hardwareUnitId}`, `{partNumber}` or
 * `{token}`, and the client substitutes by exact name.
 */
export interface EntityOperation {
  /**
   * The operation id the generated react-query helpers key their cache on — the SDK
   * function's name as a string.
   *
   * 🔴 Emitted by the generator rather than read from `call.name`, which minification
   * mangles: in a production build `geozonesGetEntity.name` is not `"geozonesGetEntity"`.
   * `useEntity` matches this against `queryKey[0]._id` to invalidate generated queries.
   */
  id: string;
  call: (options?: any) => Promise<{ data?: unknown; error?: unknown; response?: Response }>;
  param?: string;
}

/**
 * The `useEntity` slots an entity actually has, resolved to SDK functions.
 *
 * Generated per base path into `./operations` and attached to the config by
 * `defineEntity`, so `useEntity` can call a named operation instead of building a URL
 * out of `config.endpoint`. Every slot is optional: most entities have only some.
 *
 * Loosely typed on purpose. This layer moves entity traffic onto the typed client and
 * retires `fetchEGate`; inferring `T` and `TFilter` at the 295 `useEntity<T>` call
 * sites is the follow-up it unblocks, not something it does.
 */
export interface EntityOperations {
  /**
   * The entity these operations were generated for.
   *
   * 🔴 Load-bearing — see `resolveOperations`. These are the two config fields that
   * decide the API path, kept as data so the check is a field comparison rather than a
   * second implementation of the URL scheme.
   */
  entity: { endpoint: string; apiVersion: string };
  page?: EntityOperation;
  create?: EntityOperation;
  detail?: EntityOperation;
  update?: EntityOperation;
  remove?: EntityOperation;
  activate?: EntityOperation;
  list?: EntityOperation;
  export?: EntityOperation;
  valueUsed?: EntityOperation;
  containersPartial?: EntityOperation;
  trucksPartial?: EntityOperation;
  log?: EntityOperation;
  logs?: EntityOperation;
}

// ── the escape hatch ──────────────────────────────────────────────────────

declare const DELEGATED: unique symbol;

/**
 * An endpoint that is deliberately not a complete API path.
 *
 * Eight entityConfs store a *prefix* and let a call site finish it, because the real
 * URL contains a runtime id — `billing/card-openings` is requested as
 * `billing/{customerId}/card-openings` by a component that spreads the config and
 * overrides `endpoint`. Those cannot be checked against the document, so they say so
 * explicitly rather than weakening the check for the other 84.
 *
 * `npm run check-api` reports these separately; keep the two lists in agreement.
 *
 * @param prefix  the partial endpoint stored in the config
 * @param reason  where the real endpoint is built — a file path or component name
 */
export function delegatedEndpoint(prefix: string, reason: string): string & { [DELEGATED]: true } {
  void reason;
  return prefix as string & { [DELEGATED]: true };
}

type IsDelegated<E> = E extends { [DELEGATED]: true } ? true : false;

/**
 * Produced instead of the config type when the endpoint does not resolve. The
 * unsatisfiable property name is the error message the developer sees.
 */
interface EndpointNotInApiDoc<P> {
  endpoint: ["This endpoint does not exist in the OpenAPI document", P];
}

export type ValidateEndpoint<C extends { endpoint?: string }> = C extends { endpoint: infer E extends string }
  ? IsDelegated<E> extends true
    ? unknown
    : ApiOf<C> extends never
      ? EndpointNotInApiDoc<C["endpoint"]>
      : unknown
  : unknown;

// ── defineEntity ──────────────────────────────────────────────────────────

/**
 * Declares an entityConf and checks its endpoint against the OpenAPI document.
 *
 * Replaces the `const conf: EntityConfig = { … }` annotation. The annotation had to
 * go: it widened `endpoint` to `string` before anything could look at it, so no
 * check was possible. Inferring through a `const` type parameter keeps the literal.
 *
 * The return type is `C`, so the literal endpoint and version survive into consumers —
 * which is what lets a later step infer `T` and `TFilter` from the config instead of
 * taking them as hand-supplied generics.
 *
 * `name` and `singularName` are re-widened to their `EntityConfig` signatures.
 * Several configs write them as `() => ""`, and `const` inference would otherwise
 * fix the arity at zero, breaking the ~90 call sites that pass `t`.
 */
export function defineEntity<const C extends EntityConfig>(
  conf: C & ValidateEndpoint<C>
): C & Pick<EntityConfig, "name" | "singularName"> {
  return conf as C & Pick<EntityConfig, "name" | "singularName">;
}
